import cx from 'classnames'
import Icon from 'components/Icon'
import { useElementSize } from 'hooks/element-size'
import CallViewModel from 'messaging/calling/CallViewModel'
import { VideoRenderer } from 'messaging/calling/views/MediaRenderer'
import { observer } from 'mobx-react'
import * as React from 'react'
import CallMediaSettingsModal from '../CallMediaSettings'
import Action from './Action'
import styles from './ActiveCall.sass'
import CenterOfAttention from './CenterOfAttention'
import ParticipantItem from './ParticipantItem'
import StatusBar from './StatusBar'

interface ActiveCallProps {
  vm: CallViewModel
}

/**
 * The ActiveCall
 */
export default observer(function ActiveCall({ vm }: ActiveCallProps) {
  const currentUserParticipant = vm.currentUserParticipant
  const ref = React.useRef(null)
  const size = useElementSize(ref)
  const compact = size.height < 420 // nice

  React.useEffect(() => {
    vm.activeCallMounted()
    return () => vm.activeCallUnmounted()
  }, [])

  return (
    <div className={cx(styles.root)} ref={ref} data-test="active-call">
      <div className={styles.background}>
        {vm.centerOfAttention?.media?.camera && (
          <VideoRenderer vm={vm.centerOfAttention} />
        )}
      </div>
      <div className={styles.foreground}>
        <StatusBar vm={vm} />
        <div className={styles.topBar}>
          <div className={styles.settingsButtonContainer}>
            <a className={styles.settingsButton}>
              <Icon
                name="settings"
                onClick={() => vm.callMediaSettingsModalViewModel.show()}
              />
            </a>
          </div>
          <Spacer />
          <div className={styles.participants}>
            {vm.participants.map((participantVM) =>
              // Don't render one for the current user.
              participantVM.participant.member.isCurrentUser ? null : (
                <ParticipantItem
                  key={participantVM.participant.userId}
                  vm={participantVM}
                  videoless={participantVM === vm.centerOfAttention}
                />
              )
            )}
          </div>
        </div>

        <div className={styles.center}>
          {!compact &&
            vm.centerOfAttention &&
            !vm.centerOfAttention.media?.camera && (
              <CenterOfAttention vm={vm.centerOfAttention} />
            )}
        </div>

        <div className={styles.footer}>
          <Spacer />
          <div className={styles.actions}>
            <Action
              active={!currentUserParticipant?.media?.muted}
              onClick={vm.toggleMicrophone}
              disabled={vm.toggleMicrophone.pending}
              tooltip={
                currentUserParticipant?.media?.muted
                  ? 'Unmute Microphone'
                  : 'Mute Microphone'
              }
            >
              <Icon
                name={
                  currentUserParticipant?.media?.muted
                    ? 'call-mic-off'
                    : 'call-mic-on'
                }
              />
            </Action>
            <Action
              primary
              danger
              onClick={vm.hangup}
              tooltip="Leave Call"
              dataTest="end-call"
            >
              <Icon name="call-end-call" />
            </Action>
            {vm.canEnableCamera && (
              <Action
                onClick={vm.toggleCamera}
                active={currentUserParticipant?.media?.camera}
                disabled={vm.toggleCamera.pending}
                tooltip={
                  currentUserParticipant?.media?.camera
                    ? 'Disable Camera'
                    : 'Enable Camera'
                }
              >
                <Icon
                  name={
                    currentUserParticipant?.media?.camera
                      ? 'call-video-on'
                      : 'call-video-off'
                  }
                />
              </Action>
            )}
            {/* 
              // Maybe one day
              <Action>
                <Icon name="call-screenshare-off" />
              </Action>
              <Action>
                <Icon name="call-fullscreen-off" />
              </Action> */}
          </div>
          <Spacer>
            <div className={styles.currentUser}>
              {currentUserParticipant && (
                <ParticipantItem vm={currentUserParticipant} />
              )}
            </div>
          </Spacer>
        </div>
      </div>

      <CallMediaSettingsModal vm={vm.callMediaSettingsModalViewModel} />
    </div>
  )
})

interface SpacerProps {
  children?: React.ReactNode
}

/**
 " Spacer
 */
export function Spacer({ children }: SpacerProps) {
  return <div className={styles.spacer}>{children}</div>
}
